import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useApp } from '@/hooks/use-app-context';
import { useToast } from '@/hooks/use-toast';
import { audioApi } from '@/api/audio.api';
import { wordApi } from '@/api/word.api';
import { progressApi } from '@/api/progress.api';
import { getAudioExtension } from '@/config/audio.config';
import { phraseListErrorMessage } from '@/api/phrase-list.contract';
import {
  rememberCollectorModuleContribution,
  resolveCollectorContributionContext,
} from '@/lib/community-collector.storage';
import { useValidationGate, isCrossValidationBlockedError } from '@/features/validation-gate';
import { isNetworkError, isOfflineQueueError, submitOrQueue } from '@/features/offline';
import type { ValidationGate } from '@/types/auth.types';
import type { CollectionPlace } from '@/features/collection-place/collection-place.contract';
import type { ReRecordState, UploadTiming, WordItem } from '../types';
import { extractErrorMessage, normalizeWordItems } from '../utils';
import {
  SPEAK_PAGE_SIZE,
  applyReturnedTranslation,
  buildSpeakUpload,
  completedFor,
  globalPhraseIndex,
  mergeSavedTranslations,
  nextSpeakStep,
  parseSavedProgress,
  previousSpeakStep,
  rememberReturnedTranslation,
  resumeFromLastIndex,
  speakStorageKeys,
  type ReturnedTranslation,
} from '../speak-session.logic';
import { readSpeakBatchFromReserve, topUpSpeakReserve } from '../speak-offline-reserve';
import { useSpeakSessionDeepLink } from './useSpeakSessionDeepLink';
import type { SpeakRecorder } from './useSpeakRecorder';

type RecordingSlice = Pick<SpeakRecorder, 'recordedBlob' | 'recordingDurationMs' | 'resetRecording'>;

const readJson = (key: string) => {
  const raw = localStorage.getItem(key);
  return raw ? JSON.parse(raw) : {};
};

/**
 * State and steps of an "Enregistrer" session, moved out of the page with
 * the same behaviour: phrase batches of 5 with local then backend resume,
 * completed flags and translations kept in localStorage, debounced progress
 * sync, upload with the collector and collection-place context, validation
 * gate, return from transcription-phrase.
 */
export function useSpeakSession({ recording, place }: { recording: RecordingSlice; place: CollectionPlace | null }) {
  const { t, user, language } = useApp();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { lang } = useParams<{ lang: string }>();
  const currentLang = lang || language || 'fr';
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const collectorContext = useMemo(() => resolveCollectorContributionContext(location.state), [location.state]);
  const validationGate = useValidationGate();
  const [blockedDialogGate, setBlockedDialogGate] = useState<ValidationGate | null>(null);

  const locationState = location.state as ReRecordState | null;
  const reRecordData = locationState && typeof locationState === 'object' ? locationState : null;
  const returned = location.state as (Partial<ReturnedTranslation> & { targetLanguage?: string }) | null;

  const [selectedLanguage, setSelectedLanguage] = useState<string>(() => returned?.targetLanguage || searchParams.get('language') || '');
  const [page, setPage] = useState(1);
  const limit = SPEAK_PAGE_SIZE;
  const [currentSentence, setCurrentSentence] = useState(0);
  const [wordItems, setWordItems] = useState<WordItem[]>([]);
  const [completedSentences, setCompletedSentences] = useState<boolean[]>([]);
  const [sessionStarted, setSessionStarted] = useState(false);
  const [sessionFinished, setSessionFinished] = useState(false);
  const [isStartingSession, setIsStartingSession] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [progressApplied, setProgressApplied] = useState(false);
  const [uploadTiming, setUploadTiming] = useState<UploadTiming | null>(null);
  /** Takes sent during this visit, for the end-of-session screen. */
  const [uploadedCount, setUploadedCount] = useState(0);
  const isSubmittingRef = useRef(false);
  const isLoadingSentencesRef = useRef(false);
  const keys = useMemo(() => speakStorageKeys(user?.id, selectedLanguage), [user?.id, selectedLanguage]);

  const isReRecordMode = Boolean(reRecordData?.fromValidation && reRecordData?.reRecordPhrase);
  const sentences = wordItems.map((w) => w.text);
  const completedCount = completedSentences.filter(Boolean).length;
  const sessionProgress = sentences.length > 0 ? (completedCount / sentences.length) * 100 : 0;
  const alreadyDone = completedSentences[currentSentence] === true;

  // Auto-start session if returning from translation
  useEffect(() => {
    if (!sessionStarted && returned && (returned.frenchTranslation || returned.localPhrase) && selectedLanguage) {
      startSession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  // Handle return from TranscriptionPhrase with new translation
  useEffect(() => {
    const state = location.state as ReturnedTranslation | null;
    if (sessionStarted && state && (state.frenchTranslation || state.localPhrase)) {
      setWordItems((prev) => applyReturnedTranslation(prev, state));
      try {
        const key = keys.translations(page);
        localStorage.setItem(key, JSON.stringify(rememberReturnedTranslation(readJson(key), state)));
      } catch {
        // ignore storage errors
      }
      // Clear state to avoid re-applying
      window.history.replaceState({}, document.title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionStarted, location.state]);

  // Prefetch the backend progress before the session starts (no local save yet).
  useEffect(() => {
    const fetchProgress = async () => {
      if (!user?.id || !selectedLanguage || progressApplied || sessionStarted) return;
      try {
        if (localStorage.getItem(keys.progress)) return;
      } catch {
        // Ignore localStorage errors
      }
      try {
        await progressApi.getUserProgressByLang(selectedLanguage);
      } catch (error) {
        console.debug('Unable to prefetch progress data', error);
      }
    };
    fetchProgress();
  }, [user?.id, selectedLanguage, progressApplied, sessionStarted, keys.progress]);

  // Reset guard when language changes
  useEffect(() => {
    setProgressApplied(false);
  }, [selectedLanguage]);

  /** Merges the translations saved locally for this page. */
  function withSavedTranslations(items: WordItem[], pageNum: number): WordItem[] {
    try {
      const raw = localStorage.getItem(keys.translations(pageNum));
      if (raw) return mergeSavedTranslations(items, JSON.parse(raw));
    } catch {
      // ignore storage errors
    }
    return items;
  }

  async function loadSentences(newPage = 1): Promise<WordItem[] | null> {
    if (isLoadingSentencesRef.current) return null;
    isLoadingSentencesRef.current = true;
    const ownerId = user?.id != null ? String(user.id) : '';
    try {
      const data = await wordApi.getFilesByLang(selectedLanguage, newPage, limit);
      const { items } = normalizeWordItems(data, limit);
      if (!items.length) {
        toast({ title: t.noMorePhrases, description: t.noMorePhrasesDesc, variant: 'default' });
        return null;
      }
      // Keep the next phrases on the device, for when the network goes away.
      void topUpSpeakReserve(ownerId, selectedLanguage, newPage, limit).catch((error) =>
        console.debug('Unable to refresh the offline phrase reserve', error),
      );
      return withSavedTranslations(items, newPage);
    } catch (error: unknown) {
      // No network: the same batch from the phrases kept on the device.
      if (isNetworkError(error) && ownerId) {
        const kept = await readSpeakBatchFromReserve(ownerId, selectedLanguage, newPage, limit).catch(() => null);
        const { items } = normalizeWordItems(kept, limit);
        if (kept && items.length) return withSavedTranslations(items, newPage);
        toast({ title: t.loadError, description: t.offline.reserveEmpty, variant: 'destructive' });
        return null;
      }
      console.error(error);
      toast({ title: t.loadError, description: phraseListErrorMessage(error, t.phraseListErrors), variant: 'destructive' });
      return null;
    } finally {
      isLoadingSentencesRef.current = false;
    }
  }

  function persistCompleted(pageNum: number, completed: boolean[]) {
    try {
      localStorage.setItem(keys.completed, JSON.stringify({ [pageNum]: completed }));
    } catch {
      // ignore storage errors
    }
  }

  function loadCompleted(pageNum: number, count: number): boolean[] {
    try {
      return completedFor(localStorage.getItem(keys.completed), pageNum, count);
    } catch {
      return new Array(count).fill(false);
    }
  }

  function showBatch(items: WordItem[], pageNum: number, sentence: number) {
    setWordItems(items);
    setPage(pageNum);
    setCurrentSentence(sentence);
    setCompletedSentences(loadCompleted(pageNum, items.length));
  }

  const startSession = async () => {
    if (isStartingSession) return;
    if (!selectedLanguage) {
      toast({ title: t.languageRequiredTitle, description: t.languageRequiredDesc, variant: 'destructive' });
      return;
    }
    try {
      setIsStartingSession(true);

      if (isReRecordMode && reRecordData?.reRecordPhrase) {
        setWordItems([{ text: reRecordData.reRecordPhrase }]);
        setCompletedSentences([false]);
        setCurrentSentence(0);
        setSessionStarted(true);
        return;
      }

      // Try to resume from locally saved progress first
      try {
        const savedRaw = localStorage.getItem(keys.progress);
        if (savedRaw) {
          const saved = parseSavedProgress(savedRaw);
          const items = await loadSentences(saved.page);
          if (items) {
            showBatch(items, saved.page, saved.sentence);
            setProgressApplied(true);
            setSessionStarted(true);
            return;
          }
        }
      } catch (error) {
        console.debug('Unable to restore speak session from local storage', error);
      }

      // Try to resume from saved progress on backend
      try {
        if (user?.id) {
          const progressData = await progressApi.getUserProgressByLang(selectedLanguage);
          const resume = resumeFromLastIndex(progressData?.last_index, limit);
          if (resume) {
            const items = await loadSentences(resume.page);
            if (items) {
              showBatch(items, resume.page, resume.sentence);
              setProgressApplied(true);
              setSessionStarted(true);
              return;
            }
          }
        }
      } catch (error) {
        console.debug('Falling back to the first page after progress lookup failed', error);
      }

      const items = await loadSentences(1);
      if (items) {
        showBatch(items, 1, 0);
        setSessionStarted(true);
      }
    } finally {
      setIsStartingSession(false);
    }
  };

  const deepLink = useSpeakSessionDeepLink({
    searchParams,
    setSearchParams,
    selectedLanguage,
    sessionStarted,
    isStartingSession,
    isPlaceReady: place !== null,
    startSession,
  });

  // Sync progress to backend (debounced) whenever page/sentence changes
  const progressSyncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const skipNextSyncRef = useRef(true);
  useEffect(() => {
    if (!sessionStarted || !selectedLanguage) return;
    try {
      localStorage.setItem(keys.progress, JSON.stringify({ page, currentSentence }));
    } catch (error) {
      console.debug('Unable to persist speak session progress locally', error);
    }
    // Skip the first sync after session start/resume to avoid overwriting correct progress
    if (skipNextSyncRef.current) {
      skipNextSyncRef.current = false;
      return;
    }
    if (progressSyncTimerRef.current) clearTimeout(progressSyncTimerRef.current);
    progressSyncTimerRef.current = setTimeout(() => {
      progressApi.updateUserProgress(selectedLanguage, globalPhraseIndex(page, limit, currentSentence), 0).catch((err) => {
        console.debug('Unable to sync progress to backend', err);
      });
    }, 1000);
    return () => {
      if (progressSyncTimerRef.current) clearTimeout(progressSyncTimerRef.current);
    };
  }, [page, currentSentence, keys.progress, sessionStarted, selectedLanguage, limit]);

  // Persist completedSentences to localStorage whenever it changes
  useEffect(() => {
    if (sessionStarted && completedSentences.length > 0) persistCompleted(page, completedSentences);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completedSentences, page, sessionStarted, keys.completed]);

  const validateAndNext = async () => {
    if (isSubmittingRef.current) return;
    const done = completedSentences[currentSentence] === true;

    if (!user?.id) {
      toast({ title: t.connectionRequired, description: t.connectionRequiredDesc, variant: 'destructive' });
      navigate(`/${currentLang}/login`, { state: { from: location.pathname }, replace: true });
      return;
    }

    const isCommunitySubmission = Boolean(collectorContext?.communityProfileId);
    if (!isCommunitySubmission && validationGate.isBlocked) {
      setBlockedDialogGate(validationGate.gate ?? null);
      return;
    }

    try {
      isSubmittingRef.current = true;
      setIsUploading(true);

      const current = wordItems[currentSentence];
      if (!current) {
        toast({ title: t.noPhraseAvailable, description: t.noPhraseAvailableDesc, variant: 'destructive' });
        return;
      }

      // Upload if not already done and has recording
      if (!done && recording.recordedBlob) {
        const upload = buildSpeakUpload({
          item: current,
          language: selectedLanguage,
          recordingDurationMs: recording.recordingDurationMs,
          collector: collectorContext,
          place,
        });
        if (!upload.ok) {
          // No strict mode here: read the reason without relying on narrowing.
          const { reason } = upload as Extract<typeof upload, { ok: false }>;
          if (reason === 'phraseMissing') toast({ title: t.phraseMissing, description: t.phraseMissingDesc, variant: 'destructive' });
          else toast({ title: t.durationMissing, description: t.durationMissingDesc, variant: 'destructive' });
          return;
        }

        const blob = recording.recordedBlob;
        const fileName = `audio-${Date.now()}.${getAudioExtension(blob.type)}`;
        // Without network the take is kept on the device and sent later (OfflineSyncProvider).
        const outcome = await submitOrQueue({
          ownerId: String(user.id),
          kind: 'speak-audio',
          payload: { audio: blob, fileName, data: upload.meta },
          summary: { lang: selectedLanguage, text: upload.content },
          send: (headers) => audioApi.uploadWordAudio(new File([blob], fileName, { type: blob.type }), upload.meta, { headers }),
        });

        rememberCollectorModuleContribution(collectorContext, { type: 'audio', content: upload.content });
        setUploadedCount((count) => count + 1);
        const position = globalPhraseIndex(page, limit, currentSentence);

        if (outcome.queued) {
          // The local progress is saved by the effect below; the server one follows when online.
          progressApi.updateUserProgress(selectedLanguage, position, 0).catch(() => undefined);
          toast({ title: t.offline.savedTitle, description: t.offline.savedDesc });
        } else {
          // No strict mode here: read the answer without relying on narrowing.
          const { data } = outcome as Extract<typeof outcome, { queued: false }>;
          const timing = data?.uploadTiming;
          if (timing) setUploadTiming(timing);

          await progressApi.updateUserProgress(selectedLanguage, position, 0);

          toast({
            title: t.uploadSuccess,
            description: timing ? t.uploadSuccessWithTime.replace('{time}', timing.durationSeconds) : t.uploadSuccessAnonymized,
          });
        }

        const newCompleted = [...completedSentences];
        newCompleted[currentSentence] = true;
        setCompletedSentences(newCompleted);
        persistCompleted(page, newCompleted);
      }

      recording.resetRecording();

      if (nextSpeakStep(currentSentence, sentences.length) === 'nextPage') {
        const nextPage = page + 1;
        const items = await loadSentences(nextPage);
        if (items) {
          showBatch(items, nextPage, 0);
        } else {
          toast({ title: t.sessionComplete, description: t.sessionCompleteDesc });
          setSessionFinished(true);
        }
      } else {
        setCurrentSentence((prev) => prev + 1);
      }
    } catch (error) {
      const blockedGate = isCrossValidationBlockedError(error);
      if (blockedGate) {
        validationGate.setGateFromError(blockedGate);
        setBlockedDialogGate(blockedGate);
        return;
      }
      if (isOfflineQueueError(error)) {
        toast({ title: t.genericError, description: t.offline.storageError, variant: 'destructive' });
        return;
      }
      console.error(error);
      toast({ title: t.genericError, description: extractErrorMessage(error) ?? "Échec de l'envoi de l'audio", variant: 'destructive' });
    } finally {
      setIsUploading(false);
      isSubmittingRef.current = false;
    }
  };

  const previousSentence = async () => {
    if (isLoadingSentencesRef.current) return;
    const step = previousSpeakStep(currentSentence, page);
    if (step === 'back') {
      setCurrentSentence((prev) => prev - 1);
      recording.resetRecording();
      return;
    }
    if (step === 'previousPage') {
      const prevPage = page - 1;
      const items = await loadSentences(prevPage);
      if (items) {
        showBatch(items, prevPage, items.length - 1);
        recording.resetRecording();
      }
    }
  };

  return {
    t,
    user,
    currentLang,
    collectorContext,
    validationGate,
    blockedDialogGate,
    setBlockedDialogGate,
    reRecordData,
    isReRecordMode,
    selectedLanguage,
    setSelectedLanguage,
    page,
    currentSentence,
    wordItems,
    setWordItems,
    sentences,
    completedSentences,
    completedCount,
    sessionProgress,
    alreadyDone,
    sessionStarted,
    sessionFinished,
    dismissSessionFinished: () => setSessionFinished(false),
    isStartingSession,
    isUploading,
    setIsUploading,
    uploadTiming,
    uploadedCount,
    translationsStorageKey: keys.translations,
    sessionResumeActive: deepLink.sessionResumeActive,
    returnToCurrentSession: `${location.pathname}${deepLink.sessionReturnSearch}`,
    startLinkedSession: deepLink.startLinkedSession,
    validateAndNext,
    previousSentence,
    goHome: () => navigate(`/${currentLang}`),
  };
}

export type SpeakSession = ReturnType<typeof useSpeakSession>;
